import { Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, finalize } from 'rxjs';
import { SettingService } from '../setting.service';
import { TelegramAlertName, TelegramAlerts, TelegramSupplierNoticeName } from '../setting.type';

interface AlertDef {
  name: TelegramAlertName;
  label: string;
  hint: string;
}

const ALERTS: AlertDef[] = [
  { name: 'order_balance', label: 'Add / hold orders', hint: 'When the orders vs. buy contracts verdict changes and holds for 3 minutes' },
  { name: 'card_shortage', label: 'Card shortage', hint: 'When manual buy contracts keep running out of cards, and when it recovers' },
  { name: 'failed_orders', label: 'Failed orders', hint: 'Each order that fails, with its reason' },
  { name: 'job_health', label: 'Job health', hint: 'Background jobs that gave up, and card allocation stopping' },
  { name: 'payment_requests', label: 'Payment requests', hint: 'A digest of new requests and the waiting queue' },
  { name: 'crypto_payouts', label: 'Crypto payouts', hint: 'On-chain payouts that failed, got stuck or were not recorded, and the hot wallet running low on BNB' },
];

interface NoticeDef {
  name: TelegramSupplierNoticeName;
  label: string;
  hint: string;
}

const SUPPLIER_NOTICES: NoticeDef[] = [
  { name: 'stock_problems', label: 'Account problems', hint: 'An account stopped with something the supplier must fix (password, backup codes, coins…)' },
  { name: 'transfer_runs', label: 'Transfers', hint: 'A transfer run moved coins, and what it added to their balance' },
  { name: 'payouts', label: 'Payouts', hint: 'A payment, bonus or correction was paid, with the tracking number' },
  { name: 'payment_rejections', label: 'Rejected payment requests', hint: 'With the reason the admin gave' },
  { name: 'contracts', label: 'Contracts', hint: 'A manual buy contract completed, failed or expired' },
  { name: 'buying_status', label: 'Buying on / off', hint: 'Sent to every linked supplier when buying is switched on or off' },
];

/**
 * The admin Telegram group's alerts: each switch saves at once, since these
 * are operational switches rather than configuration the page saves together.
 */
@Component({
  selector: 'app-telegram-alerts',
  imports: [FormsModule, MatSlideToggleModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule],
  templateUrl: './telegram-alerts.html',
  styleUrl: './telegram-alerts.scss',
})
export class TelegramAlertsPanel implements OnInit {
  @Input() canEdit = false;

  private settingService = inject(SettingService);
  private snackBar = inject(MatSnackBar);

  readonly alerts = ALERTS;
  readonly supplierNotices = SUPPLIER_NOTICES;
  config: TelegramAlerts | null = null;
  configured = false;
  digestMinutes = 60;
  /** The topic fields as typed; null (empty) is General. */
  topics: Partial<Record<TelegramAlertName, number | null>> = {};
  saving = false;
  testing = false;

  ngOnInit(): void {
    forkJoin({ config: this.settingService.getTelegramAlerts(), status: this.settingService.getTelegramStatus() })
      .subscribe({
        next: ({ config, status }) => {
          this.apply(config);
          this.configured = status.configured;
        },
        error: () => this.fail('Could not load Telegram alerts'),
      });
  }

  toggle(name: TelegramAlertName, on: boolean): void {
    this.save({ alerts: { [name]: on } }, on ? 'Alert on' : 'Alert off');
  }

  toggleSupplierBot(on: boolean): void {
    this.save({ supplier_bot_enabled: on }, on ? 'Supplier bot on' : 'Supplier bot off');
  }

  toggleNotice(name: TelegramSupplierNoticeName, on: boolean): void {
    this.save({ supplier_notices: { [name]: on } }, on ? 'Notice on' : 'Notice off');
  }

  saveTopic(name: TelegramAlertName): void {
    if (!this.config) return;
    const typed = this.topics[name];
    const topic = typed == null ? 0 : typed;
    if (!Number.isInteger(topic) || topic < 0) {
      this.topics[name] = this.config.topics[name] || null;
      this.fail('A topic is a whole number; leave it empty for General');
      return;
    }
    if (topic === this.config.topics[name]) return;
    this.save({ topics: { [name]: topic } }, topic ? `Posts in topic ${topic}` : 'Posts in General');
  }

  saveDigest(): void {
    if (!this.config || this.digestMinutes < 5 || this.digestMinutes === this.config.payment_digest_minutes) return;
    this.save({ payment_digest_minutes: this.digestMinutes }, 'Digest interval saved');
  }

  sendTest(): void {
    this.testing = true;
    this.settingService.sendTelegramTest()
      .pipe(finalize(() => this.testing = false))
      .subscribe({
        next: () => this.snackBar.open('Test message sent — check each topic in the admin group', 'Close', { duration: 4000 }),
        error: (err: HttpErrorResponse) => this.fail(err.error?.error ?? 'Could not send the test message'),
      });
  }

  private save(request: Parameters<SettingService['updateTelegramAlerts']>[0], done: string): void {
    this.saving = true;
    this.settingService.updateTelegramAlerts(request)
      .pipe(finalize(() => this.saving = false))
      .subscribe({
        next: config => {
          this.apply(config);
          this.snackBar.open(done, 'Close', { duration: 2500 });
        },
        error: (err: HttpErrorResponse) => {
          // Put the switch back where the server has it.
          if (this.config) this.apply({ ...this.config });
          this.fail(err.error?.error ?? 'Could not save Telegram alerts');
        },
      });
  }

  private apply(config: TelegramAlerts): void {
    this.config = config;
    this.digestMinutes = config.payment_digest_minutes;
    this.topics = {};
    for (const a of ALERTS) this.topics[a.name] = config.topics?.[a.name] || null;
  }

  private fail(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
  }
}
